import { useAppTheme } from '@shared-ui/theme'
import { rh, rw } from '@shared-ui/theme/scale'
import { memo, useEffect, useId, useMemo } from 'react'
import { Animated, View } from 'react-native'
import { SvgXml } from 'react-native-svg'
import { markPath } from './mark-source'
import { useCreateStyles } from './styles'

export type StartupMarkProps = {
  arrival: Animated.Value
  reveal: Animated.Value
  sheen: Animated.Value
  isReduceMotion: boolean
  onImageSettled: () => void
}

export const StartupMark = memo(function StartupMarkComponent({
  arrival,
  reveal,
  sheen,
  isReduceMotion,
  onImageSettled,
}: StartupMarkProps) {
  const styles = useCreateStyles()
  const { COLOR_TEXT_PRIMARY } = useAppTheme()
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')

  useEffect(() => {
    onImageSettled()
  }, [onImageSettled])

  const markXml = useMemo(
    () =>
      `<svg xmlns="http://www.w3.org/2000/svg" width="732.695" height="563.398" viewBox="0 0 732.695 563.398" fill="none">` +
      `<path d="${markPath}" fill="${COLOR_TEXT_PRIMARY}"/>` +
      `</svg>`,
    [COLOR_TEXT_PRIMARY],
  )

  // SVG остаётся неизменным: нативный драйвер смешивает готовые слои света.
  const lightLayers = useMemo(
    () =>
      [0.2, 0.4, 0.6, 0.8].map((position, index) => {
        const gradientId = `startupLight${id}${index}`
        const center = -260 + position * 1250
        const xml = `<svg xmlns="http://www.w3.org/2000/svg" width="732.695" height="563.398" viewBox="0 0 732.695 563.398">
        <defs><linearGradient id="${gradientId}" gradientUnits="userSpaceOnUse" x1="${center - 160}" y1="0" x2="${center + 160}" y2="0" gradientTransform="skewX(-15)">
          <stop stop-color="white" stop-opacity="0"/>
          <stop offset="0.5" stop-color="white" stop-opacity="0.12"/>
          <stop offset="1" stop-color="white" stop-opacity="0"/>
        </linearGradient></defs>
        <path d="${markPath}" fill="url(#${gradientId})"/>
      </svg>`
        return { position, xml }
      }),
    [id],
  )

  return (
    <Animated.View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[
        styles.markStage,
        {
          opacity: arrival.interpolate({ inputRange: [0, 0.3, 1], outputRange: [0, 0.8, 1] }),
          transform: isReduceMotion
            ? []
            : [
                { perspective: rw(900) },
                { translateY: arrival.interpolate({ inputRange: [0, 1], outputRange: [rh(12), 0] }) },
                { rotateY: arrival.interpolate({ inputRange: [0, 1], outputRange: ['-14deg', '0deg'] }) },
                { scale: arrival.interpolate({ inputRange: [0, 1], outputRange: [0.92, 1] }) },
                { scale: reveal.interpolate({ inputRange: [0, 1], outputRange: [1, 1.035] }) },
              ],
        },
      ]}
    >
      <View renderToHardwareTextureAndroid shouldRasterizeIOS>
        <SvgXml xml={markXml} width={rw(220)} height={rw(170)} />
      </View>
      {!isReduceMotion &&
        lightLayers.map(({ position, xml }) => (
          <Animated.View
            key={position}
            pointerEvents="none"
            renderToHardwareTextureAndroid
            shouldRasterizeIOS
            style={[
              styles.markLight,
              {
                opacity: sheen.interpolate({
                  inputRange: [position - 0.2, position, position + 0.2],
                  outputRange: [0, 1, 0],
                  extrapolate: 'clamp',
                }),
              },
            ]}
          >
            <SvgXml xml={xml} width={rw(220)} height={rw(170)} />
          </Animated.View>
        ))}
    </Animated.View>
  )
})
